/**
 * SuperAdminSidebar (v6.32.99) — Left-rail navigation for the Super Admin
 * Platform Control Center.
 *
 * Replaces the v6.32.98 horizontal 31-tab `<TabsList>` strip with a
 * task-based grouped sidebar following the patterns used by Stripe,
 * Linear, and Salesforce Lightning. Research-driven decisions:
 *
 *   - 6 task-named groups (Hick's Law / Miller's Law: 7 ± 2 items per
 *     navigation level) — each group expands to 4-6 tabs.
 *   - Sticky search bar at top — recognition over recall (AWS Console
 *     pattern). Type any tab name to filter.
 *   - Default state: only the group containing the currently active
 *     tab is expanded — minimizes initial cognitive load.
 *   - Mobile (<768px): collapses to a hamburger drawer.
 *   - Every existing data-testid is preserved verbatim so the test
 *     suite continues to pass and the GuidedTour selectors keep
 *     working.
 *
 * The Radix `<Tabs>` / `<TabsContent>` infrastructure in
 * SuperAdminDashboard.js is unchanged — only the visual rendering of
 * the tab list itself is replaced. This is a strict refactor of the
 * navigation surface; zero changes to any tab's content, business
 * logic, route, or API contract.
 */
import React, { useState, useMemo, useEffect } from 'react';
import WedgeHealthDot from './WedgeHealthDot';
import {
  AlertTriangle,
  BarChart3,
  Bookmark,
  Building2,
  ChevronDown,
  ChevronRight,
  Cpu,
  CreditCard,
  Database,
  DollarSign,
  Download,
  FileText,
  FlaskConical,
  GraduationCap,
  Home,
  Image as ImageIcon,
  Layers,
  Lock,
  Megaphone,
  Menu,
  Newspaper,
  Package,
  ShieldAlert,
  BookOpenCheck,
  Presentation,
  Printer,
  School,
  ScrollText,
  Search,
  Shield,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
  Wrench,
  X,
} from 'lucide-react';

/**
 * The canonical 6-group taxonomy. Adding a new tab? Place it in the
 * group that matches the OPERATOR'S TASK (what they're trying to do),
 * not the technical feature category.
 */
export const SUPER_ADMIN_TAB_GROUPS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: Home,
    tabs: [
      { value: 'overview', label: 'Overview', icon: BarChart3, testId: 'sa-overview-tab' },
      { value: 'launch-readiness', label: 'Launch Readiness', icon: ShieldCheck, testId: 'sa-launch-tab' },
      { value: 'anomaly-report', label: 'Anomaly Report', icon: AlertTriangle, testId: 'sa-anomaly-tab' },
      { value: 'infrastructure', label: 'Infrastructure', icon: Cpu, testId: 'sa-infra-tab' },
    ],
  },
  {
    id: 'customers',
    label: 'Customers',
    icon: Users,
    tabs: [
      { value: 'districts', label: 'Districts', icon: Building2, testId: 'sa-districts-tab' },
      { value: 'schools', label: 'Schools', icon: School, testId: 'sa-schools-tab' },
      { value: 'beta-schools', label: 'Beta Schools', icon: FlaskConical, testId: 'sa-beta-tab' },
      { value: 'users', label: 'Users', icon: Users, testId: 'sa-users-tab' },
      { value: 'teacher-portal', label: 'Teacher Portal', icon: GraduationCap, testId: 'sa-teacher-portal-tab' },
      { value: 'leads', label: 'Leads', icon: Megaphone, testId: 'sa-leads-tab' },
    ],
  },
  {
    id: 'sales',
    label: 'Sales & Revenue',
    icon: DollarSign,
    tabs: [
      { value: 'sample-demos', label: 'Sample Demos', icon: Package, testId: 'sa-sample-demos-tab' },
      { value: 'sealed-usb', label: '📦 USB Orders', icon: Package, testId: 'sa-sealed-usb-tab' },
      { value: 'quotes', label: 'Quotes', icon: FileText, testId: 'sa-quotes-tab' },
      { value: 'orders', label: 'Orders', icon: Package, testId: 'sa-orders-tab' },
      { value: 'pos', label: 'Licenses & POs', icon: CreditCard, testId: 'sa-pos-tab' },
      { value: 'licenses', label: 'Licenses', icon: Lock, testId: 'sa-licenses-tab' },
      { value: 'commissions', label: 'Commissions', icon: DollarSign, testId: 'sa-commissions-tab' },
      { value: 'sales-collateral', label: 'Sales & Marketing', icon: Megaphone, testId: 'sa-collateral-tab' },
      { value: 'social-media', label: 'Social Media Studio', icon: Sparkles, testId: 'sa-social-media-tab' },
      { value: 'supplemental-solutions', label: 'Supplemental Solutions', icon: Package, testId: 'sa-supplemental-solutions-tab' },
      { value: 'sales-reps', label: 'Sales Reps', icon: Users, testId: 'sa-sales-reps-tab' },
      { value: 'playbook', label: 'Rep Portal', icon: Presentation, testId: 'sa-playbook-tab' },
    ],
  },
  {
    id: 'content',
    label: 'Content',
    icon: Bookmark,
    tabs: [
      { value: 'content-studio', label: 'Content Studio', icon: Layers, testId: 'sa-content-studio-tab' },
      { value: 'big-picture-gallery', label: 'Big Pictures', icon: ImageIcon, testId: 'sa-gallery-tab' },
      { value: 'print-materials', label: 'Print (BLMs)', icon: Printer, testId: 'sa-print-tab' },
      { value: 'download-activity', label: 'Download Activity', icon: Download, testId: 'sa-downloads-tab' },
      { value: 'content-auditor', label: 'Content Auditor', icon: ShieldCheck, testId: 'sa-content-auditor-tab' },
      { value: 'coverage', label: 'Coverage', icon: Layers, testId: 'sa-coverage-tab' },
      { value: 'print-production', label: 'Print Production', icon: Printer, testId: 'sa-print-production-tab' },
      { value: 'summative-authoring', label: 'Summative Authoring', icon: Sparkles, testId: 'sa-summative-authoring-tab' },
      { value: 'entering-level', label: 'Entering-Level Studio', icon: Layers, testId: 'sa-entering-level-tab', path: '/entering-level' },
      { value: 'content-catalogue', label: 'Content Integrity Catalogue', icon: BookOpenCheck, testId: 'sa-content-catalogue-tab', path: '/audit' },
    ],
  },
  {
    id: 'compliance',
    label: 'Compliance',
    icon: Shield,
    tabs: [
      { value: 'audit', label: 'Audit Log', icon: FileText, testId: 'sa-audit-tab' },
      { value: 'compliance', label: 'ERMA / Compliance', icon: Shield, testId: 'sa-compliance-tab' },
      { value: 'erma-compliance', label: 'ERMA', icon: Shield, testId: 'sa-erma-tab' },
      { value: 'benchmarking', label: 'Benchmarking', icon: TrendingUp, testId: 'sa-benchmarking-tab' },
      { value: 'proficiency-watch', label: 'Proficiency Watch', icon: Newspaper, testId: 'sa-proficiency-watch-tab' },
      { value: 'live-data-guard', label: 'Live Data Guard', icon: ShieldAlert, testId: 'sa-live-data-guard-tab' },
      { value: 'parity-audit', label: 'Parity Audit', icon: BookOpenCheck, testId: 'sa-parity-audit-tab' },
      { value: 'audit-review', label: 'Audit Review', icon: ShieldCheck, testId: 'sa-audit-review-tab' },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: Wrench,
    tabs: [
      { value: 'operations', label: 'Operations', icon: Wrench, testId: 'sa-operations-tab' },
      { value: 'maintenance', label: 'Maintenance', icon: Database, testId: 'sa-maintenance-tab' },
      { value: 'restore-points', label: 'Restore Points', icon: ShieldCheck, testId: 'sa-restore-points-tab' },
      { value: 'variant-authoring', label: 'Variant Authoring', icon: Sparkles, testId: 'sa-variant-authoring-tab' },
      { value: 'ab-testing', label: 'A/B Tests', icon: FlaskConical, testId: 'sa-ab-tab' },
      { value: 'demo-data', label: 'Demo Data', icon: Database, testId: 'sa-demo-data-tab' },
      { value: 'demo-accounts', label: 'Demo Accounts', icon: ShieldCheck, testId: 'sa-demo-accounts-tab' },
      { value: 'changelog', label: 'Changelog', icon: ScrollText, testId: 'sa-changelog-tab' },
      { value: 'team-updates', label: 'Team Updates', icon: Megaphone, testId: 'sa-updates-tab' },
    ],
  },
];

const findGroupForTab = (tabValue) => {
  for (const group of SUPER_ADMIN_TAB_GROUPS) {
    if (group.tabs.some((t) => t.value === tabValue)) return group.id;
  }
  return SUPER_ADMIN_TAB_GROUPS[0].id;
};

const SuperAdminSidebar = ({ activeTab, onTabChange }) => {
  const [search, setSearch] = useState('');
  const [openGroups, setOpenGroups] = useState(() => ({
    [findGroupForTab(activeTab)]: true,
  }));
  const [mobileOpen, setMobileOpen] = useState(false);

  // When activeTab changes externally, ensure its group is expanded.
  useEffect(() => {
    const groupId = findGroupForTab(activeTab);
    setOpenGroups((prev) => (prev[groupId] ? prev : { ...prev, [groupId]: true }));
  }, [activeTab]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return SUPER_ADMIN_TAB_GROUPS;
    return SUPER_ADMIN_TAB_GROUPS
      .map((group) => {
        const matchedTabs = group.tabs.filter(
          (t) =>
            t.label.toLowerCase().includes(q) ||
            t.value.toLowerCase().includes(q) ||
            group.label.toLowerCase().includes(q),
        );
        return { ...group, tabs: matchedTabs };
      })
      .filter((group) => group.tabs.length > 0);
  }, [search]);

  // When searching, expand every group that has a match so the user
  // sees all results at once.
  const effectiveOpenGroups = useMemo(() => {
    if (!search.trim()) return openGroups;
    const all = {};
    filteredGroups.forEach((g) => { all[g.id] = true; });
    return all;
  }, [search, openGroups, filteredGroups]);

  const handleTabClick = (tab) => {
    if (tab && tab.path) {
      window.location.href = tab.path;
      return;
    }
    const tabValue = typeof tab === 'string' ? tab : tab.value;
    onTabChange(tabValue);
    setMobileOpen(false);
  };

  const toggleGroup = (groupId) => {
    setOpenGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const sidebarContent = (
    <>
      <div className="px-4 pt-4 pb-3 border-b border-gray-200">
        <div className="relative" data-testid="sa-sidebar-search-wrap">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tabs…"
            aria-label="Search Super Admin tabs"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 bg-gray-50 focus:bg-white focus:border-red-600 focus:outline-none transition-colors"
            data-testid="sa-sidebar-search"
          />
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-2" aria-label="Super Admin navigation">
        {filteredGroups.length === 0 && (
          <div className="px-4 py-6 text-sm text-gray-500 text-center" data-testid="sa-sidebar-no-results">
            No tabs match "<span className="font-mono">{search}</span>"
          </div>
        )}
        {filteredGroups.map((group) => {
          const GroupIcon = group.icon;
          const isOpen = !!effectiveOpenGroups[group.id];
          return (
            <div key={group.id} className="mb-1">
              <button
                type="button"
                onClick={() => toggleGroup(group.id)}
                className="w-full flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                aria-expanded={isOpen}
                data-testid={`sa-sidebar-group-${group.id}`}
              >
                <GroupIcon size={14} />
                <span className="flex-1 text-left">{group.label}</span>
                {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
              {isOpen && (
                <ul className="mt-0.5 mb-2">
                  {group.tabs.map((tab) => {
                    const TabIcon = tab.icon;
                    const isActive = activeTab === tab.value;
                    return (
                      <li key={tab.value}>
                        <button
                          type="button"
                          onClick={() => handleTabClick(tab)}
                          className={`w-full flex items-center gap-2 pl-10 pr-4 py-2 text-sm transition-colors border-l-2 ${
                            isActive
                              ? 'bg-red-50 text-red-700 font-semibold border-red-600'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 border-transparent'
                          }`}
                          aria-current={isActive ? 'page' : undefined}
                          data-testid={tab.testId}
                        >
                          <TabIcon size={14} className="flex-shrink-0" />
                          <span className="text-left">{tab.label}</span>
                          {tab.path && (
                            <span className="ml-auto text-[10px] text-gray-400">↗</span>
                          )}
                          {/* v6.35.9 — health dot only on the Content Auditor entry */}
                          {tab.value === 'content-auditor' && (
                            <span className="ml-auto pr-1">
                              <WedgeHealthDot />
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </nav>
    </>
  );

  return (
    <>
      {/* v6.34.14 — Top-left hamburger (primary mobile trigger, visible <768px).
          Mirrors the universal mobile mental model. The previous fixed bottom-right
          pill stays as a secondary thumb-friendly trigger for one-handed phone use. */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed top-3 left-3 z-30 flex items-center justify-center w-11 h-11 bg-red-600 text-white shadow-lg rounded-lg"
        aria-label="Open Super Admin navigation"
        data-testid="sa-sidebar-mobile-open-top"
      >
        <Menu size={22} />
      </button>

      {/* Mobile hamburger toggle (secondary — bottom-right floating pill, visible <768px) */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed bottom-6 right-6 z-30 flex items-center gap-2 px-4 py-3 bg-red-600 text-white shadow-lg rounded-full"
        aria-label="Open Super Admin navigation"
        data-testid="sa-sidebar-mobile-open"
      >
        <Menu size={18} />
        <span className="text-sm font-semibold">Menu</span>
      </button>

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile drawer */}
      <aside
        className={`md:hidden fixed top-0 left-0 bottom-0 w-72 bg-white z-50 shadow-xl transition-transform duration-200 flex flex-col ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sa-sidebar-mobile"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <span className="text-sm font-bold uppercase tracking-wide text-red-600">Super Admin</span>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="p-1 hover:bg-gray-100 rounded"
            aria-label="Close navigation"
            data-testid="sa-sidebar-mobile-close"
          >
            <X size={18} />
          </button>
        </div>
        {sidebarContent}
      </aside>

      {/* Desktop / tablet sidebar (visible ≥768px — v6.34.14 expanded from lg≥1024 to md≥768 so tablets get the full sidebar instead of the drawer) */}
      <aside
        className="hidden md:flex flex-col w-60 flex-shrink-0 bg-white border-r border-gray-200 sticky top-0 self-start"
        style={{ height: 'calc(100vh - 4rem)' }}
        data-testid="sa-sidebar-desktop"
      >
        {sidebarContent}
      </aside>
    </>
  );
};

export default SuperAdminSidebar;
